import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import {
	Pipeline,
	ProspectSteps,
	getPipelineDetailFRM,
	savePipelineFRM,
	updatePipelineFRM,
} from '@/libs/fleet-api/services/pipeline';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	notification,
	Result,
	Row,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment, useState } from 'react';
import PipelineMainInformation from '../../../components/pipeline/PipelineMainInformation';
import fetcher from '@/libs/fleet-api/fetcher';
import FleetTitle from '@/libs/antd/components/Title';
import { formatCurrency } from '@/libs/antd/formater';
import DataRow from '@/libs/antd/components/DataRow';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type FRMPipelinePageProps = {
	prospectId: string;
};

const FRMPipelinePage: FC<FRMPipelinePageProps> = ({ prospectId }) => {
	const { navigate } = useApp();

	const [isSavingMode, setIsSavingMode] = useState<boolean>(false);
	const [successUpdateModal, setSuccessUpdateModal] = useState<boolean>(false);
	const [successSaveModal, setSuccessSaveModal] = useState<boolean>(false);
	const [pipelineNo, setPipelineNo] = useState<string | null>(null);

	const { form, fieldName } = useForm<Pipeline<'payload'>>();

	const updateFRMPipelineMutation = useMutation({
		mutationFn: updatePipelineFRM,
	});

	const saveFRMPipelineMutation = useMutation({
		mutationFn: savePipelineFRM,
	});

	const { data, isLoading, refetch, isError } = useQuery({
		queryKey: ['getPipelineDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPipelineDetailFRM(id);
		},
		enabled: !!prospectId,
	});

	const onUpdate = async () => {
		try {
			await updateFRMPipelineMutation.mutateAsync({
				...form.getFieldsValue(),
				prospectId,
				...(data?.data.pipelineDetail.pipelineHotProspectId
					? { id: data?.data.pipelineDetail.pipelineHotProspectId }
					: {}),
			});
			refetch();
			setSuccessUpdateModal(true);
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const onSubmit = async (values: Pipeline<'payload'>) => {
		try {
			const response = await saveFRMPipelineMutation.mutateAsync({
				...values,
				prospectId,
				...(data?.data.pipelineDetail.pipelineHotProspectId
					? { id: data?.data.pipelineDetail.pipelineHotProspectId }
					: {}),
			});
			if (response.data) {
				console.log(response.data);
				if (response.data?.pipelineNo) {
					setPipelineNo(response?.data.pipelineNo);
				}
			}
			setSuccessSaveModal(true);
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (!data) return null;

	const { pipelineDetail, customerDocuments, prospectInformation } = data.data;

	if (isError) return <GenericErrorMessage />;
	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetModal
				open={successUpdateModal}
				okText="NEXT"
				onOk={() => {
					setSuccessUpdateModal(false);
					return navigate('FRMPipelineListPage');
				}}
				onCancel={() => setSuccessUpdateModal(false)}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses Mengupdate</div>}
				/>
			</FleetModal>
			<FleetModal
				open={successSaveModal}
				okText="NEXT"
				onOk={() => {
					setSuccessSaveModal(false);
					navigate('FRMPipelineListPage');
				}}
				onCancel={() => {
					setSuccessSaveModal(false);
					navigate('FRMPipelineListPage');
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<Result
					status="success"
					title={
						<div>
							<div style={{ fontSize: 20 }}>Pipeline ID Hot Prospect Anda</div>
							<div style={{ fontSize: 32 }}>{pipelineNo}</div>
						</div>
					}
				/>
			</FleetModal>
			<PipelineMainInformation
				mainInformation={{
					prospectId,
					prospectStatus: pipelineDetail?.prospectStatus.value ?? '',
					orderSource: pipelineDetail?.orderSource?.value ?? '',
					customerId: pipelineDetail?.customerId ?? '',
					customerName: pipelineDetail?.customerName ?? '',
					customerGroup: pipelineDetail?.customerGroup ?? { id: 0, value: '' },
					industrySector: pipelineDetail?.industrySector ?? {
						id: 0,
						value: '',
					},
					slikResult: '',
					frm: { id: 0, value: '' },
					creditAnalyst: { id: 0, value: '' },
					existingPlafond: pipelineDetail?.existingFinancingLimit ?? 0,
					appliedPlafond: pipelineDetail?.limitApplication ?? 0,
					applicationStatus: pipelineDetail?.prospectStatus.value ?? '',
					newOrderDate: pipelineDetail?.newOrderDate ?? '',
					outstandingPrincipal: pipelineDetail?.outstandingPrincipal ?? 0,
				}}
				customerBusinessDocuments={customerDocuments.customerBusinessDocuments}
				customerLegalDocuments={customerDocuments.customerLegalDocuments}
				customerReferralDocuments={customerDocuments.customerReferralDocuments}
				isLoading={isLoading}
				prospectStatusId={
					(pipelineDetail?.prospectStatus.id ??
						ProspectSteps.Bch) as unknown as ProspectSteps
				}
			/>

			<Divider />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="productOffer"
				onFinish={(values) => onSubmit(values)}
				initialValues={{
					ultimateOwner: pipelineDetail?.ultimateOwner,
					currentIssue: pipelineDetail?.currentIssue,
					creditScheme: pipelineDetail?.creditScheme,
					pipelinePlafonTypeIds: pipelineDetail?.pipelinePlafonTypeIds ?? [],
				}}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('ultimateOwner')}
							label="Ultimate Owner"
							labelCol={{ span: 10 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>

				<Divider />
				<Row
					gutter={[20, 40]}
					style={{ marginBottom: 20 }}
				>
					<Col span={24}>
						<Form.Item
							name={fieldName('pipelinePlafonTypeIds')}
							label="Jenis Plafon"
							labelCol={{ span: 5 }}
							wrapperCol={{
								style: { marginLeft: -10 },
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<Checkbox.Group>
								<Flex gap={20}>
									{prospectInformation?.financingApplicationLimitInformation?.map(
										(plafon) => (
											<Checkbox
												value={plafon.plafonType?.id}
												key={plafon.prospectPlafonId}
											>
												{plafon.plafonType?.value}
											</Checkbox>
										),
									)}
								</Flex>
							</Checkbox.Group>
						</Form.Item>
					</Col>
				</Row>

				{prospectInformation?.financingApplicationLimitInformation?.map(
					(plafon) => (
						<div key={plafon.prospectPlafonId}>
							<FleetTitle level={3}>
								Plafon {plafon.plafonType?.value}
							</FleetTitle>
							<DataRow
								style={{ marginBottom: 20 }}
								items={[
									[
										[
											'Outstanding Principal',
											formatCurrency(plafon.outstandingPrincipal),
										],
										[
											'Plafond Existing',
											formatCurrency(plafon.existingFinancingLimit),
										],
									],
									[
										[
											'Plafon Diajukan',
											formatCurrency(plafon?.limitApplication),
										],
										['Detail Sumber Order', plafon.referrer?.unitName],
									],
								]}
							/>
						</div>
					),
				)}

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('currentIssue')}
							label="Current Issue"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('creditScheme')}
							label="Skema Kredit"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row
					gutter={[20, 40]}
					align="middle"
					style={{
						marginBottom: 24,
					}}
				>
					<Col span={24}>
						<Checkbox
							onChange={(e) => {
								setIsSavingMode(e.target.checked);
								form.validateFields(); // Revalidate fields when checkbox changes
							}}
						>
							Checklist kotak disamping jika data terkait{' '}
							<strong style={{ color: '#1557BC' }}>Current Issue</strong> dan{' '}
							<strong style={{ color: '#1557BC' }}>Skema Kredit</strong> telah
							siap untuk dibahas di forum pipeline.
						</Checkbox>
					</Col>
				</Row>

				<Flex
					justify="flex-end"
					gap={12}
				>
					<Button
						type="primary"
						loading={updateFRMPipelineMutation.isPending}
						style={{
							width: 200,
							display: 'block',
							marginTop: 30,
							borderColor: '#1557BC',
							background: 'white',
							color: '#1557BC',
							fontWeight: 600,
						}}
						onClick={() => onUpdate()}
					>
						UPDATE
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={saveFRMPipelineMutation.isPending}
						style={{
							width: 200,
							display: 'block',
							marginTop: 30,
						}}
						disabled={!isSavingMode}
					>
						SAVE
					</Button>
				</Flex>
			</Form>
		</Fragment>
	);
};

export default FRMPipelinePage;
